import React, { useState } from 'react'
import { Button, IconButton, Pill, TextField } from '../ui'

function RemoveButton({ report, onRemoveReport }) {
  const [confirming, setConfirming] = useState(false)

  if (confirming) {
    return (
      <div style={styles.confirmInline} onClick={(e) => e.stopPropagation()}>
        <span style={styles.confirmText}>Remove {report.reportName || report.reportEmail}?</span>
        <Button size="sm" variant="danger" onClick={() => onRemoveReport(report.reportId)}>
          Remove
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setConfirming(false)}>
          Cancel
        </Button>
      </div>
    )
  }

  return (
    <IconButton
      ariaLabel={`Remove ${report.reportEmail}`}
      onClick={(e) => { e.stopPropagation(); setConfirming(true) }}
    >×</IconButton>
  )
}

export function ReportSelector({ reports, selectedReportId, onSelectReport, onAddReport, onRemoveReport, onClickReport, showRemove = false, showAdd = true, renderBadge }) {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  async function handleAdd(e) {
    e.preventDefault()
    if (!email.trim()) return
    setLoading(true)
    setError(null)
    try {
      await onAddReport(email.trim())
      setEmail('')
    } catch (err) {
      setError(err?.message || 'User not found')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={styles.container} data-testid="report-selector">
      {reports.length === 0 ? (
        <p style={styles.empty}>No direct reports yet</p>
      ) : (
        reports.map((r) => (
          <div key={r.reportId} style={styles.row}>
            <button
              style={{
                ...styles.item,
                background: selectedReportId === r.reportId ? 'var(--border)' : 'transparent',
                cursor: r.status === 'pending' ? 'default' : 'pointer',
                opacity: r.status === 'pending' ? 0.7 : 1,
              }}
              onClick={() => onClickReport ? onClickReport(r) : onSelectReport(r.reportId)}
              disabled={r.status === 'pending' && !onClickReport}
            >
              <span style={styles.nameRow}>
                <span style={styles.name}>{r.reportName || r.reportEmail}</span>
                {r.status === 'pending' && <Pill tone="warning">Pending</Pill>}
              </span>
              <span style={styles.email}>{r.reportEmail}</span>
              {renderBadge && renderBadge(r)}
              {r.tags?.length > 0 && (
                <div style={styles.tagRow}>
                  {r.tags.map((tag) => <span key={tag} style={styles.tagChip}>{tag}</span>)}
                </div>
              )}
            </button>
            {showRemove && (
              <RemoveButton report={r} onRemoveReport={onRemoveReport} />
            )}
          </div>
        ))
      )}
      {showAdd && (
        <>
          <form onSubmit={handleAdd} style={styles.form}>
            <TextField
              type="email"
              placeholder="Add by email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              style={{ flex: 1, minWidth: 0 }}
            />
            <Button type="submit" size="sm" disabled={loading || !email.trim()}>
              {loading ? '…' : '+'}
            </Button>
          </form>
          {error && <p style={styles.error}>{error}</p>}
        </>
      )}
    </div>
  )
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: '2px' },
  row: { display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap' },
  item: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '10px 12px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    textAlign: 'left',
    gap: '2px',
    background: 'transparent',
  },
  removeBtn: {
    background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px',
    color: 'var(--text-secondary)', padding: '10px 6px', lineHeight: 1, borderRadius: '4px',
  },
  confirmInline: {
    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
    width: '100%', padding: '6px 12px 10px', fontSize: '12px',
  },
  confirmText: { fontSize: '12px', color: 'var(--text-primary)', flex: 1 },
  nameRow: { display: 'flex', alignItems: 'center', gap: '6px' },
  name: { fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' },
  email: { fontSize: '11px', color: 'var(--text-secondary)' },
  tagRow: { display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' },
  tagChip: {
    fontSize: '10px', padding: '2px 6px', borderRadius: '10px',
    background: 'var(--border)', color: 'var(--text-secondary)',
  },
  empty: { fontSize: '12px', color: 'var(--text-secondary)', padding: '4px 0 8px' },
  form: { display: 'flex', gap: '4px', marginTop: '6px' },
  addBtn: {
    padding: '6px 10px', fontSize: '14px', fontWeight: 600, border: 'none',
    borderRadius: '4px', background: 'var(--text-primary)', color: '#fff',
    cursor: 'pointer', lineHeight: 1,
  },
  error: { fontSize: '11px', color: 'var(--error, #e53e3e)', marginTop: '4px' },
}
